import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
  Paper,
  Chip,
  Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parse, isValid } from "date-fns";
import { Close } from "@mui/icons-material";
import { FilterConfig, FilterRowConfig, ItemFilterRule } from "../types/AdHocTypes";

interface FilterRowProps {
  id: string;
  filterType: string;
  config: FilterRowConfig;
  filterConfigs: FilterConfig[];
  onRemove: () => void;
  onUpdate: (updates: Partial<{ filterType: string; config: FilterRowConfig }>) => void;
  onOpenItemFilter: (rowId: string, currentRules: ItemFilterRule[], onSave: (rules: ItemFilterRule[]) => void) => void;
}

const FilterRow: React.FC<FilterRowProps> = ({
  id,
  filterType,
  config,
  filterConfigs,
  onRemove,
  onUpdate,
  onOpenItemFilter,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedConfig, setSelectedConfig] = useState<FilterConfig | null>(null);

  useEffect(() => {
    const configData = filterConfigs.find((f) => f.id === filterType);
    setSelectedConfig(configData || null);
  }, [filterType, filterConfigs]);

  const handleFilterTypeChange = (event: SelectChangeEvent) => {
    const newFilterType = event.target.value;

    onUpdate({
      filterType: newFilterType,
      config: {}, // Reset config when filter type changes
    });
  };

  const handleConfigChange = (key: string, value: any) => {
    // When operator changes, reset value for certain cases
    if (key === "operator") {
      const newConfig = { ...config, [key]: value };

      // For boolean operators, don't need to reset value since it's handled automatically
      // For other operator changes, keep existing value unless it's a significant change
      onUpdate({
        config: newConfig,
      });
    } else {
      onUpdate({
        config: { ...config, [key]: value },
      });
    }
  };

  const handleItemFilterOpen = () => {
    onOpenItemFilter(id, config.itemRules || [], (rules: ItemFilterRule[]) => {
      handleConfigChange("itemRules", rules);
    });
  };

  const renderOperatorSelect = (operators: string[]) => (
    <FormControl size="small" sx={{ minWidth: 300 }}>
      <Select
        value={config.operator || ""}
        onChange={(e) => handleConfigChange("operator", e.target.value)}
        displayEmpty
      >
        <MenuItem value="">{t("adHocSegments.selectOperator")}</MenuItem>
        {operators.map((op) => (
          <MenuItem key={op} value={op}>
            {op}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderFilterContent = () => {
    if (!selectedConfig) return null;

    // Show operator selection for all field types
    const operatorSelect = renderOperatorSelect(selectedConfig.operators || []);

    // Determine if we need to show value input based on operator and field type
    const needsValueInput = () => {
      if (!config.operator) return false;

      // For boolean fields with "Is true" or "Is false", no value needed
      if (selectedConfig.dataType === "BOOLEAN" && ["Is true", "Is false"].includes(config.operator)) {
        return false;
      }

      return true;
    };

    const renderValueInput = () => {
      if (!needsValueInput()) {
        return (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", padding: theme.spacing(1) }}>
            ✓ No additional value needed
          </Typography>
        );
      }

      // Check for "between" operators - show two input fields
      const isBetweenOperator = config.operator && config.operator.toLowerCase().includes("between");

      if (isBetweenOperator) {
        // Parse current value (format: "x,y")
        const values = config.value
          ? String(config.value)
              .split(",")
              .map((v: string) => v.trim())
          : ["", ""];
        const fromValue = values[0] || "";
        const toValue = values[1] || "";

        // Determine input type based on field dataType
        const inputType =
          selectedConfig.dataType === "NUMBER" ||
          selectedConfig.dataType === "DECIMAL" ||
          selectedConfig.dataType === "AGE"
            ? "number"
            : "text";

        // Validation: check if "from" is greater than "to" for numeric fields
        const hasValidationError =
          inputType === "number" && fromValue && toValue && Number.parseFloat(fromValue) > Number.parseFloat(toValue);

        return (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              size="small"
              type={inputType}
              placeholder={t("adHocSegments.from")}
              value={fromValue}
              onChange={(e) => {
                const newValue = `${e.target.value},${toValue}`;
                handleConfigChange("value", newValue);
              }}
              sx={{ width: 100 }}
            />
            <Typography variant="body2" color="text.secondary">
              {t("adHocSegments.to")}
            </Typography>
            <TextField
              size="small"
              type={inputType}
              placeholder={t("adHocSegments.to")}
              value={toValue}
              onChange={(e) => {
                const newValue = `${fromValue},${e.target.value}`;
                handleConfigChange("value", newValue);
              }}
              sx={{ width: 100 }}
            />
            {hasValidationError && (
              <Typography variant="caption" color="error">
                ⚠ {t("adHocSegments.fromGreaterThanTo")}
              </Typography>
            )}
          </Box>
        );
      }

      // Check if this field has allowed values (enum)
      if (selectedConfig.allowedValues && selectedConfig.allowedValues.length > 0) {
        // For multi-select operators like "Is in list"
        const isMultiSelectOperator = config.operator && ["Is in list", "Is not in list"].includes(config.operator);

        if (isMultiSelectOperator) {
          // Parse current value (could be comma-separated string or array)
          const currentValue = Array.isArray(config.value)
            ? config.value
            : config.value
            ? String(config.value)
                .split(",")
                .map((v: string) => v.trim())
            : [];

          return (
            <Autocomplete
              multiple
              size="small"
              options={selectedConfig.allowedValues}
              value={currentValue}
              onChange={(_, newValue) => {
                // Store as comma-separated string for API compatibility
                handleConfigChange("value", newValue.join(","));
              }}
              renderInput={(params) => <TextField {...params} placeholder={t("adHocSegments.selectValues")} />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => <Chip label={option} size="small" {...getTagProps({ index })} />)
              }
              sx={{ minWidth: 250 }}
            />
          );
        } else {
          // Single select for other operators
          return (
            <FormControl size="small" sx={{ minWidth: 300 }}>
              <Select
                value={config.value || ""}
                onChange={(e) => handleConfigChange("value", e.target.value)}
                displayEmpty
              >
                <MenuItem value="">{t("adHocSegments.selectValue")}</MenuItem>
                {selectedConfig.allowedValues.map((allowedValue) => (
                  <MenuItem key={allowedValue} value={allowedValue}>
                    {allowedValue}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
      }

      switch (selectedConfig.inputType) {
        case "text":
          return (
            <TextField
              size="small"
              type="text"
              placeholder={t("adHocSegments.enterValue")}
              value={config.value || ""}
              onChange={(e) => handleConfigChange("value", e.target.value)}
              sx={{ minWidth: 300 }}
            />
          );

        case "number":
          return (
            <TextField
              size="small"
              type="number"
              placeholder={t("adHocSegments.enterValue")}
              value={config.value || ""}
              onChange={(e) => handleConfigChange("value", e.target.value)}
              sx={{ minWidth: 300 }}
            />
          );

        case "date":
          const isAgeRelatedOperator =
            config.operator &&
            [
              "Age greater than",
              "Age equals",
              "Age less than",
              "Age between",
              "Years ago",
              "Days ago",
              "Months ago",
              "Within last N days",
              "Within last N months",
              "Within last N years",
            ].includes(config.operator);

          if (isAgeRelatedOperator) {
            return (
              <TextField
                size="small"
                type="number"
                placeholder={config.operator?.includes("Age") ? "Age" : "Number"}
                value={config.value || ""}
                onChange={(e) => handleConfigChange("value", e.target.value)}
                sx={{ minWidth: 300 }}
              />
            );
          } else {
            return (
              <DatePicker
                value={config.value ? parse(config.value, "yyyy-MM-dd", new Date()) : null}
                onChange={(date) => {
                  if (date && isValid(date)) {
                    // Store in ISO format (yyyy-MM-dd) for API compatibility
                    const isoDate = format(date as Date, "yyyy-MM-dd");
                    handleConfigChange("value", isoDate);
                  } else {
                    handleConfigChange("value", "");
                  }
                }}
                inputFormat="dd.MM.yyyy"
                renderInput={(params) => <TextField {...params} size="small" placeholder="dd.mm.yyyy" sx={{ minWidth: 300 }} />}
              />
            );
          }

        case "boolean":
          return (
            <FormControl size="small" sx={{ minWidth: 300 }}>
              <Select
                value={config.value || ""}
                onChange={(e) => handleConfigChange("value", e.target.value)}
                displayEmpty
              >
                <MenuItem value="">{t("adHocSegments.selectValue")}</MenuItem>
                <MenuItem value="true">Yes / True</MenuItem>
                <MenuItem value="false">No / False</MenuItem>
              </Select>
            </FormControl>
          );

        default:
          return (
            <TextField
              size="small"
              type="text"
              placeholder={t("adHocSegments.enterValue")}
              value={config.value || ""}
              onChange={(e) => handleConfigChange("value", e.target.value)}
              sx={{ minWidth: 300 }}
            />
          );
      }
    };

    // Handle special cases that need different UI layouts
    switch (selectedConfig.inputType) {
      case "modal-item-filter":
        const ruleCount = config.itemRules?.length || 0;
        return (
          <Button variant="contained" color="primary" onClick={handleItemFilterOpen}>
            {t("adHocSegments.defineItemFilter")}
            {ruleCount > 0 && <Chip label={`${ruleCount} ${t("adHocSegments.rulesDefined")}`} size="small" sx={{ marginLeft: 1 }} />}
          </Button>
        );

      default:
        // Standard layout: Filter Type -> Operator -> Value (if needed)
        return (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            {operatorSelect}
            {config.operator && renderValueInput()}
          </Box>
        );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper sx={{ p: theme.spacing(2), mb: theme.spacing(1) }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          {/* Filter Type Selector */}
          <FormControl size="small" sx={{ minWidth: 350 }}>
            <Select value={filterType} onChange={handleFilterTypeChange} displayEmpty>
              <MenuItem value="">{t("adHocSegments.selectFilter")}</MenuItem>
              {filterConfigs.map((config) => (
                <MenuItem key={config.id} value={config.id}>
                  {config.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Dynamic Filter Content */}
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>{renderFilterContent()}</Box>

          {/* Remove Button */}
          <IconButton
            onClick={onRemove}
            size="small"
            sx={{
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: theme.palette.error.light,
                color: theme.palette.error.dark,
              }
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </LocalizationProvider>
  );
};

export default FilterRow;
